import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WixEntryLoader from '../components/WixEntryLoader.js';
import { completeWixConnect, finishWixConnect } from '../utils/wixConnect';
import './WixConnect.css';

export default function WixConnect() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [legacyConnect, setLegacyConnect] = useState(null);
  const navigate = useNavigate();
  // Guard against firing the connect POST twice. The OAuth `code` is single-use, and
  // React 18 StrictMode runs this effect twice on mount in dev — the second call would
  // race the first, reuse the spent code, and surface a (harmless) 400. One submit only.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const params = new URLSearchParams(window.__UB_WIX_CALLBACK__?.path === '/wix-connect'
      ? window.__UB_WIX_CALLBACK__.search : window.location.search);
    if (['code', 'state', 'instanceId'].some(key => params.getAll(key).length > 1)) {
      setError('Invalid Wix connection parameters.'); setLoading(false); return;
    }
    const code = params.get('code');
    const stateParam = params.get('state');
    const instanceId = params.get('instanceId');
    if ((stateParam || '').startsWith('pc_')) {
      completeWixConnect(params).then(result => {
        delete window.__UB_WIX_CALLBACK__;
        finishWixConnect(result);
      }).catch(err => { setError(err.message); setLoading(false); });
      return;
    }

    let botId = null;
    let userId = null;
    let returnTo = null; // optional 3rd segment: an in-app path to go back to (agent-creation flow)

    if (stateParam) {
      const decoded = stateParam;
      const parts = decoded.split(':');
      [botId, userId] = parts;
      const rt = parts.slice(2).join(':');
      if (rt.startsWith('/agents/new/')) returnTo = rt;
    }

    if (!code) {
      setError(t('wix_connect_no_code'));
      setLoading(false);
      return;
    }

    const connectWix = async () => {
      try {
        setLegacyConnect(null); setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}auth/wix/connect_bot?code=${encodeURIComponent(code)}&bot_id=${encodeURIComponent(botId)}&instance_id=${encodeURIComponent(instanceId)}&user_id=${encodeURIComponent(userId)}&confirmed=true`, {
          method: 'POST', headers: {Authorization: `Bearer ${localStorage.getItem('token') || ''}`},
        });

        if (!res.ok) {
          throw new Error('Wix authorization failed');
        }

        await res.json();
        
        // Only try to close if opened as popup, otherwise navigate
        if (window.opener) {
          window.close();
        } else {
          navigate(returnTo || '/');
        }
      } catch (err) {
        console.error(err);
        setError(t('wix_connect_auth_failed'));
      } finally {
        setLoading(false);
      }
    };

    // Old-format in-flight callbacks carry unsigned user/bot IDs. Require an
    // explicit action; all newly initiated connections use server-bound state.
    setLegacyConnect(() => connectWix);
    setLoading(false);
  }, [navigate, t]);

  // Same branded full-screen loader as the Wix App Market entry + onboarding
  // (purple grid background, white ring, caption) so the connect step is visually
  // continuous with the rest of the Wix flow.
  if (loading) return <WixEntryLoader text={t('wix_connect_connecting')} />;
  if (legacyConnect) return <div className="wixc-error"><div className="wixc-error-card">
    <h2>Confirm your Wix connection</h2><p>This connection was started before the portal update. Continue only if you just connected this agent from Wix.</p>
    <button className="wixc-button" onClick={legacyConnect}>Confirm connection</button>
    <button className="wixc-button wixc-button-secondary" onClick={() => navigate('/deploy/channels')}>Back to Channels</button>
  </div></div>;

  if (error) {
    return (
      <div className="wixc-error">
        <div className="wixc-error-card">
          <h2>{t('wix_connect_error_title')}</h2>
          <p>{error}</p>
          <button className="wixc-button" onClick={() => navigate('/deploy/channels')}>Back to Channels</button>
        </div>
      </div>
    );
  }

  return null;
}
